import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Tag,
  Filter,
  RefreshCw,
  CheckCircle,
  XCircle,
  Eye,
  Package,
  Image as ImageIcon,
  ImagePlus,
} from "lucide-react";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { brandService, Brand, BrandFilters, BrandDetails, CreateBrandRequest, UpdateBrandRequest } from "@/services/brandService";

export default function Brands() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // State management
  const [search, setSearch] = useState('');
  const [isActive, setIsActive] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [editFormData, setEditFormData] = useState<UpdateBrandRequest>({});
  const [createFormData, setCreateFormData] = useState<CreateBrandRequest>({
    name: '',
    name_ar: '',
    logo_url: '',
    is_active: true,
  });
  const [createLogoUploading, setCreateLogoUploading] = useState(false);
  const [editLogoUploading, setEditLogoUploading] = useState(false);

  // Build filters object for API
  const filters: BrandFilters = useMemo(() => {
    const filterObj: BrandFilters = {
      page: currentPage,
      limit: limit
    };
    if (search) filterObj.q = search;
    if (isActive !== 'all') filterObj.is_active = isActive;
    return filterObj;
  }, [currentPage, limit, search, isActive]);

  // React Query for brands data
  const { data: brandsResponse, isLoading: brandsLoading, error: brandsError, refetch: refetchBrands } = useQuery({
    queryKey: ['brands', filters],
    queryFn: () => brandService.getBrands(filters),
    staleTime: 30000, // 30 seconds
  });

  // Extract brands and pagination from response
  const brands = useMemo(() => {
    return brandsResponse?.success && brandsResponse?.data ? brandsResponse.data.items : [];
  }, [brandsResponse]);

  const pagination = useMemo(() => {
    return brandsResponse?.success && brandsResponse?.data ? {
      total: brandsResponse.data.total,
      totalPages: brandsResponse.data.totalPages,
      currentPage: brandsResponse.data.page,
      perPage: brandsResponse.data.pageSize
    } : {
      total: 0,
      totalPages: 0,
      currentPage: 1,
      perPage: 50
    };
  }, [brandsResponse]);

  // React Query for brand statistics
  const { data: statsResponse, isLoading: statsLoading } = useQuery({
    queryKey: ['brandStats'],
    queryFn: () => brandService.getBrandStats(),
    staleTime: 60000, // 1 minute
  });

  const brandStats = useMemo(() => {
    return statsResponse?.success && statsResponse?.data ? statsResponse.data : {
      total: 0,
      active: 0,
      inactive: 0
    };
  }, [statsResponse]);

  const isLoading = brandsLoading || statsLoading;

  // React Query for brand details
  const { data: brandDetailsResponse, isLoading: detailsLoading } = useQuery({
    queryKey: ['brandDetails', selectedBrand?.id],
    queryFn: () => brandService.getBrandById(selectedBrand!.id),
    enabled: !!selectedBrand && isViewDialogOpen,
    staleTime: 30000,
  });

  const brandDetails = useMemo(() => {
    return brandDetailsResponse?.success && brandDetailsResponse?.data 
      ? brandDetailsResponse.data 
      : null;
  }, [brandDetailsResponse]);

  // Mutations
  const createBrandMutation = useMutation({
    mutationFn: (data: CreateBrandRequest) => brandService.createBrand(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      queryClient.invalidateQueries({ queryKey: ['brandStats'] });
      setIsCreateDialogOpen(false);
      setCreateFormData({
        name: '',
        name_ar: '',
        logo_url: '',
        is_active: true,
      });
      toast({
        title: t("success") || "Success",
        description: t("brandCreated") || "Brand created successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error") || "Error",
        description: error?.message || t("failedToCreateBrand") || "Failed to create brand",
        variant: "destructive",
      });
    },
  });

  const updateBrandMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBrandRequest }) =>
      brandService.updateBrand(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      queryClient.invalidateQueries({ queryKey: ['brandStats'] });
      queryClient.invalidateQueries({ queryKey: ['brandDetails'] });
      setIsEditDialogOpen(false);
      setEditFormData({});
      setSelectedBrand(null);
      toast({
        title: t("success") || "Success",
        description: t("brandUpdated") || "Brand updated successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error") || "Error",
        description: error?.message || t("failedToUpdateBrand") || "Failed to update brand",
        variant: "destructive",
      });
    },
  });

  const deleteBrandMutation = useMutation({
    mutationFn: (id: string) => brandService.deleteBrand(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brands'] });
      queryClient.invalidateQueries({ queryKey: ['brandStats'] });
      toast({
        title: t("success") || "Success",
        description: t("brandDeleted") || "Brand deleted successfully",
      });
    },
    onError: (error: any) => {
      toast({
        title: t("error") || "Error",
        description: error?.message || t("failedToDeleteBrand") || "Failed to delete brand",
        variant: "destructive",
      });
    },
  });

  // Handle filter changes - reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setIsActive(value);
    setCurrentPage(1);
  };

  // Event handlers
  const handleViewBrand = (brand: Brand) => {
    setSelectedBrand(brand);
    setIsViewDialogOpen(true);
  };

  const handleEditBrand = (brand: Brand) => {
    setSelectedBrand(brand);
    setEditFormData({
      name: brand.name,
      name_ar: brand.name_ar,
      logo_url: brand.logo_url,
      is_active: brand.is_active,
    });
    setIsEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    if (!selectedBrand) return;
    updateBrandMutation.mutate({ id: selectedBrand.id, data: editFormData });
  };

  const handleCreateBrand = () => {
    if (!createFormData.name || !createFormData.name_ar) {
      toast({
        title: t("validationError") || "Validation Error",
        description: t("pleaseCompleteAllFields") || "Please complete all required fields",
        variant: "destructive",
      });
      return;
    }
    createBrandMutation.mutate(createFormData);
  };

  const handleDeleteBrand = (brand: Brand) => {
    const confirmMessage = t("confirmDeleteBrand") || `Are you sure you want to delete brand "${brand.name}"?`;
    if (confirm(confirmMessage)) {
      deleteBrandMutation.mutate(brand.id);
    }
  };

  const uploadBrandLogo = async (
    file: File,
    setUploading: (v: boolean) => void,
    onUrl: (url: string) => void
  ) => {
    setUploading(true);
    try {
      const res = await brandService.uploadImage(file);
      if (res.success && res.data?.url) {
        onUrl(res.data.url);
      } else {
        toast({ title: t("error") || "Error", description: res.message || "Upload failed", variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: t("error") || "Error", description: e?.message || "Upload failed", variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("brandsManage") || "Brands Manage"}</h1>
          <p className="text-muted-foreground mt-1">
            {t("manageBrands") || "Manage product brands"}
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {t("addBrand") || "Add Brand"}
        </Button>
      </div>

      {/* Statistics Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("totalBrands") || "Total Brands"}</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{isLoading ? "..." : brandStats.total}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("activeBrands") || "Active Brands"}</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{isLoading ? "..." : brandStats.active}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{t("inactiveBrands") || "Inactive Brands"}</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{isLoading ? "..." : brandStats.inactive}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="mr-2 h-5 w-5" />
            {t("searchAndFilters") || "Search and Filters"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="search">{t("search") || "Search"}</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder={t("searchBrandsPlaceholder") || "Search by name..."}
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t("status") || "Status"}</Label>
              <Select value={isActive || 'all'} onValueChange={handleStatusChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t("selectStatus") || "Select Status"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allStatuses") || "All Statuses"}</SelectItem>
                  <SelectItem value="true">{t("active") || "Active"}</SelectItem>
                  <SelectItem value="false">{t("inactive") || "Inactive"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>{t("pageSize") || "Items per page"}</Label>
              <Select value={limit.toString()} onValueChange={(value) => {
                setLimit(Math.min(parseInt(value), 100));
                setCurrentPage(1);
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Brands Table */}
      <Card>
        <CardHeader>
          <CardTitle>{t("brands") || "Brands"} ({isLoading ? "..." : pagination.total})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("brandLogo") || "Logo"}</TableHead>
                <TableHead>{t("brandName") || "Name"}</TableHead>
                <TableHead>{t("brandNameAr") || "Name (AR)"}</TableHead>
                <TableHead>{t("status") || "Status"}</TableHead>
                <TableHead>{t("createdAt") || "Created At"}</TableHead>
                <TableHead className="text-right">{t("actions") || "Actions"}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex items-center justify-center">
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      {t("loading")}...
                    </div>
                  </TableCell>
                </TableRow>
              ) : brands.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="text-muted-foreground">
                      {t("noBrandsFound") || "No brands found"}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                brands.map((brand) => (
                  <TableRow key={brand.id}>
                    <TableCell>
                      {brand.logo_url ? (
                        <img 
                          src={brand.logo_url} 
                          alt={brand.name}
                          className="h-10 w-10 object-contain rounded"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="h-10 w-10 bg-muted rounded flex items-center justify-center">
                          <ImageIcon className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{brand.name}</TableCell>
                    <TableCell className="font-medium">{brand.name_ar}</TableCell>
                    <TableCell>
                      <Badge variant={brand.is_active ? "default" : "secondary"}>
                        {brand.is_active ? t("active") || "Active" : t("inactive") || "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(brand.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title={t("viewBrand") || "View Brand"}
                          onClick={() => handleViewBrand(brand)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title={t("editBrand") || "Edit Brand"}
                          onClick={() => handleEditBrand(brand)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          title={t("deleteBrand") || "Delete Brand"}
                          onClick={() => handleDeleteBrand(brand)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          {pagination.totalPages > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-4">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                {t("showing") || "Showing"} {(pagination.currentPage - 1) * pagination.perPage + 1}–{Math.min(pagination.currentPage * pagination.perPage, pagination.total)} {t("of") || "of"} {pagination.total} {t("brands") || "brands"}
              </div>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || isLoading}
                >
                  {t("previous") || "Prev"}
                </Button>

                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={isLoading}
                        className="w-9"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <span className="sm:hidden text-sm px-2 text-muted-foreground">
                  {currentPage} / {pagination.totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))}
                  disabled={currentPage === pagination.totalPages || isLoading}
                >
                  {t("next") || "Next"}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Brand Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t("brandDetails") || "Brand Details"}</DialogTitle>
            <DialogDescription>
              {t("viewBrandInformation") || "View detailed brand information"}
            </DialogDescription>
          </DialogHeader>
          
          {brandDetails ? (
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                {brandDetails.logo_url ? (
                  <img 
                    src={brandDetails.logo_url} 
                    alt={brandDetails.name}
                    className="h-20 w-20 object-contain rounded border"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="h-20 w-20 bg-muted rounded flex items-center justify-center border">
                    <ImageIcon className="h-10 w-10 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold">{brandDetails.name}</h3>
                  <p className="text-muted-foreground">{brandDetails.name_ar}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("brandName") || "Name"}</Label>
                  <p className="text-sm font-medium">{brandDetails.name}</p>
                </div>
                <div>
                  <Label>{t("brandNameAr") || "Name (AR)"}</Label>
                  <p className="text-sm font-medium">{brandDetails.name_ar}</p>
                </div>
                <div>
                  <Label>{t("brandLogoUrl") || "Logo URL"}</Label>
                  <p className="text-sm break-all">{brandDetails.logo_url}</p>
                </div>
                <div>
                  <Label>{t("status") || "Status"}</Label>
                  <Badge variant={brandDetails.is_active ? "default" : "secondary"}>
                    {brandDetails.is_active ? t("active") || "Active" : t("inactive") || "Inactive"}
                  </Badge>
                </div>
                <div>
                  <Label>{t("createdAt") || "Created At"}</Label>
                  <p className="text-sm">{new Date(brandDetails.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <Label>{t("updatedAt") || "Updated At"}</Label>
                  <p className="text-sm">{new Date(brandDetails.updatedAt).toLocaleString()}</p>
                </div>
              </div>

              {brandDetails.Products && brandDetails.Products.length > 0 && (
                <div className="space-y-2">
                  <Label className="flex items-center">
                    <Package className="mr-2 h-4 w-4" />
                    {t("products") || "Products"} ({brandDetails.Products.length})
                  </Label>
                  <div className="border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("productName") || "Product Name"}</TableHead>
                          <TableHead>{t("status") || "Status"}</TableHead>
                          <TableHead>{t("stock") || "Stock"}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {brandDetails.Products.map((product) => (
                          <TableRow key={product.id}>
                            <TableCell className="font-medium">{product.name}</TableCell>
                            <TableCell>
                              <Badge variant={product.status === "active" ? "default" : "secondary"}>
                                {product.status}
                              </Badge>
                            </TableCell>
                            <TableCell>{product.stock}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          ) : detailsLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              {t("loading")}...
            </div>
          ) : null}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsViewDialogOpen(false)}>
              {t("close") || "Close"}
            </Button>
            {selectedBrand && (
              <Button onClick={() => {
                setIsViewDialogOpen(false);
                handleEditBrand(selectedBrand);
              }}>
                <Edit className="mr-2 h-4 w-4" />
                {t("edit") || "Edit"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Brand Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("addBrand") || "Add Brand"}</DialogTitle>
            <DialogDescription>
              {t("createNewBrand") || "Create a new brand"}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-name">{t("brandName") || "Name"} *</Label>
              <Input
                id="create-name"
                value={createFormData.name}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t("enterBrandName") || "Enter brand name"}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="create-name-ar">{t("brandNameAr") || "Name (AR)"} *</Label>
              <Input
                id="create-name-ar"
                value={createFormData.name_ar}
                onChange={(e) => setCreateFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                placeholder={t("enterBrandNameAr") || "Enter brand name in Arabic"}
              />
            </div>
            
            <div className="space-y-2">
              <Label>{t("brandLogo") || "Logo"}</Label>
              <div className="flex items-center gap-3">
                {createFormData.logo_url ? (
                  <img src={createFormData.logo_url} alt="preview" className="h-16 w-16 rounded object-contain border" />
                ) : (
                  <div className="h-16 w-16 bg-muted rounded flex items-center justify-center border">
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <label className="flex items-center gap-2 cursor-pointer px-4 py-2 border rounded-md hover:bg-muted transition-colors">
                  {createLogoUploading ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <ImagePlus className="h-4 w-4" />
                  )}
                  <span className="text-sm">{createLogoUploading ? (t("uploading") || "Uploading...") : (t("uploadLogo") || "Upload Logo")}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={createLogoUploading}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) uploadBrandLogo(file, setCreateLogoUploading, (url) => setCreateFormData(prev => ({ ...prev, logo_url: url })));
                      e.target.value = '';
                    }}
                  />
                </label>
                {createFormData.logo_url && (
                  <Button variant="ghost" size="sm" onClick={() => setCreateFormData(prev => ({ ...prev, logo_url: '' }))}>
                    {t("remove") || "Remove"}
                  </Button>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>{t("status") || "Status"}</Label>
              <Select 
                value={createFormData.is_active.toString()} 
                onValueChange={(value) => setCreateFormData(prev => ({ ...prev, is_active: value === 'true' }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">{t("active") || "Active"}</SelectItem>
                  <SelectItem value="false">{t("inactive") || "Inactive"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsCreateDialogOpen(false);
              setCreateFormData({
                name: '',
                name_ar: '',
                logo_url: '',
                is_active: true,
              });
            }}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              onClick={handleCreateBrand}
              disabled={createBrandMutation.isPending || createLogoUploading}
            >
              {createBrandMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("creating") || "Creating..."}
                </>
              ) : (
                <>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("createBrand") || "Create Brand"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Brand Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("editBrand") || "Edit Brand"}</DialogTitle>
            <DialogDescription>
              {t("updateBrandInformation") || "Update brand information"}
            </DialogDescription>
          </DialogHeader>
          
          {selectedBrand && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">{t("brandName") || "Name"} *</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder={t("enterBrandName") || "Enter brand name"}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-name-ar">{t("brandNameAr") || "Name (AR)"} *</Label>
                <Input
                  id="edit-name-ar"
                  value={editFormData.name_ar || ''}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                  placeholder={t("enterBrandNameAr") || "Enter brand name in Arabic"}
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t("brandLogo") || "Logo"}</Label>
                <div className="flex items-center gap-3">
                  {editFormData.logo_url ? (
                    <img src={editFormData.logo_url} alt="preview" className="h-16 w-16 rounded object-contain border" />
                  ) : (
                    <div className="h-16 w-16 bg-muted rounded flex items-center justify-center border">
                      <ImageIcon className="h-6 w-6 text-muted-foreground" />
                    </div>
                  )}
                  <label className="flex items-center gap-2 cursor-pointer px-4 py-2 border rounded-md hover:bg-muted transition-colors">
                    {editLogoUploading ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <ImagePlus className="h-4 w-4" />
                    )}
                    <span className="text-sm">{editLogoUploading ? (t("uploading") || "Uploading...") : (t("uploadLogo") || "Upload Logo")}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={editLogoUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadBrandLogo(file, setEditLogoUploading, (url) => setEditFormData(prev => ({ ...prev, logo_url: url })));
                        e.target.value = '';
                      }}
                    />
                  </label>
                  {editFormData.logo_url && (
                    <Button variant="ghost" size="sm" onClick={() => setEditFormData(prev => ({ ...prev, logo_url: '' }))}>
                      {t("remove") || "Remove"}
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>{t("status") || "Status"}</Label>
                <Select 
                  value={editFormData.is_active !== undefined ? editFormData.is_active.toString() : 'true'} 
                  onValueChange={(value) => setEditFormData(prev => ({ ...prev, is_active: value === 'true' }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">{t("active") || "Active"}</SelectItem>
                    <SelectItem value="false">{t("inactive") || "Inactive"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setIsEditDialogOpen(false);
              setEditFormData({});
              setSelectedBrand(null);
            }}>
              {t("cancel") || "Cancel"}
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={updateBrandMutation.isPending || editLogoUploading}
            >
              {updateBrandMutation.isPending ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  {t("saving") || "Saving..."}
                </>
              ) : (
                <>
                  <Edit className="mr-2 h-4 w-4" />
                  {t("saveChanges") || "Save Changes"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
